import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #drawerReads = new Set();

  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const controller of this.#drawerReads) controller.abort();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const update = this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // Drawer rows and the empty state are committed only by a confirmed server render.
    if (this.isDrawer) return update;

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.isDrawer) return this.#updateDrawerQuantity(config);

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /** Confirm drawer mutations before removing any merchandise from the DOM. */
  async #updateDrawerQuantity(config) {
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    super.updatedCallback();
    const key = this.refs.cartItemRows?.[config.line - 1]?.dataset.key;
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();
    try {
      if (!key) throw new Error('Unable to identify the item to update.');
      const sections = new Set([this.sectionId]);
      for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
        if (component instanceof HTMLElement && component.dataset.sectionId) sections.add(component.dataset.sectionId);
      }
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
        body: JSON.stringify({
          line: config.line,
          quantity: config.quantity,
          sections: [...sections].join(','),
          sections_url: window.location.pathname,
        }),
      }));
      const cart = await response.json();
      if (!response.ok || cart.errors || cart.status >= 400) {
        const message = typeof cart.errors === 'string' ? cart.errors : cart.description;
        throw new Error(message || response.statusText || 'Unable to update your bag.');
      }
      await this.#applyDrawerCart(cart, cart.sections?.[this.sectionId]);
    } catch (error) {
      console.error(error);
      // A failed response can still follow a committed write. READ once; never repeat the POST.
      try {
        await this.#recoverDrawerCart();
      } catch (recoveryError) {
        console.error(recoveryError);
      }
      this.#showDrawerError(error instanceof Error ? error.message : String(error), key);
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      cartPerformance.measureFromMarker(marker);
    }
  }

  /** Validate both the authoritative count and its matching rendered merchandise before morphing. */
  async #applyDrawerCart(cart, html, didError = false) {
    if (!Number.isSafeInteger(cart?.item_count) || cart.item_count < 0 || !Array.isArray(cart.items)) {
      throw new Error('Unable to confirm the updated bag count.');
    }
    if (typeof html !== 'string' || !html.trim()) throw new Error('Unable to display the updated bag.');
    const documentHTML = new DOMParser().parseFromString(html, 'text/html');
    const section = documentHTML.getElementById(`shopify-section-${this.sectionId}`);
    const items = section?.querySelector('[data-hydration-key="cart-drawer-inner"] cart-items-component[data-drawer]');
    const rows = [...(items?.querySelectorAll('tr[data-key]') ?? [])];
    const renderedCount = items?.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    const empty = Boolean(items?.querySelector('#cart-drawer-heading-empty'));
    if (!items || rows.length !== cart.items.length || rows.some((row, index) => row.dataset.key !== cart.items[index].key ||
          Number(row.querySelector('input[ref="quantityInput"]')?.value) !== cart.items[index].quantity) ||
        (cart.item_count === 0 ? !empty || rows.length !== 0 : !renderedCount || Number(renderedCount) !== cart.item_count)) {
      throw new Error('Unable to confirm the updated bag contents.');
    }
    if (!this.isConnected) return;
    await morphSection(this.sectionId, html, { mode: 'hydration' });
    this.#refreshDrawerState(cart.item_count);
    this.#updateQuantitySelectors(cart);
    this.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, {
      itemCount: cart.item_count,
      source: 'cart-items-component',
      sections: { ...cart.sections, [this.sectionId]: html },
      didError,
    }));
  }

  /** One bounded action-level recovery, bypassing the shared pending-section cache. */
  async #recoverDrawerCart() {
    const controller = new AbortController();
    this.#drawerReads.add(controller);
    const deadline = setTimeout(() => controller.abort(), 15000);
    try {
      const cartURL = new URL(Theme.routes.cart_url, window.location.origin);
      cartURL.pathname = cartURL.pathname.replace(/\/$/, '') + '.js';
      const options = { cache: 'no-store', credentials: 'same-origin', signal: controller.signal };
      const cartResponse = await fetch(cartURL, options);
      if (!cartResponse.ok) throw new Error(cartResponse.statusText);
      const cart = await cartResponse.json();
      const sectionURL = new URL(window.location.href);
      sectionURL.searchParams.set('section_id', this.sectionId);
      const sectionResponse = await fetch(sectionURL, options);
      if (!sectionResponse.ok) throw new Error(sectionResponse.statusText);
      await this.#applyDrawerCart(cart, await sectionResponse.text(), true);
    } finally {
      clearTimeout(deadline);
      this.#drawerReads.delete(controller);
    }
  }

  /** Keep the existing empty-state styling, accessibility and quantity controls in step with the inner morph. */
  #refreshDrawerState(count) {
    super.updatedCallback();
    const dialog = this.closest('dialog');
    dialog?.classList.toggle('cart-drawer--empty', count === 0);
    dialog?.setAttribute('aria-labelledby', count === 0 ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
    this.#updateCartQuantitySelectorButtonStates();
  }

  /** Use the existing row alert where possible; retain an alert even if that line was confirmed removed. */
  #showDrawerError(message, key) {
    if (!this.isConnected) return;
    super.updatedCallback();
    const row = this.refs.cartItemRows?.find((item) => item.dataset.key === key);
    const input = row?.querySelector('input[ref="quantityInput"]');
    if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    const text = row?.querySelector('.cart-item__error-text');
    const container = row?.querySelector('.cart-items__error');
    if (text && container) {
      text.textContent = message;
      container.classList.remove('hidden');
    } else {
      let alert = this.querySelector('[data-cart-update-error]');
      if (!alert) {
        alert = document.createElement('p');
        alert.setAttribute('data-cart-update-error', '');
        alert.setAttribute('role', 'alert');
        alert.className = 'cart-item__error-text';
        this.prepend(alert);
      }
      alert.textContent = message;
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event.target === this) return;
    if (this.isDrawer) {
      let recoveryAttempted = false;
      try {
        const html = event instanceof DiscountUpdateEvent
          ? event.detail.resource?.sections?.[this.sectionId]
          : event.detail.data?.sections?.[this.sectionId];
        if (typeof html !== 'string' || !html.trim()) {
          recoveryAttempted = true;
          await this.#recoverDrawerCart();
          return;
        }
        // The badge consumes this same event's native delta. Never overwrite it with a full-header morph first.
        await morphSection(this.sectionId, html, { mode: 'hydration' });
        super.updatedCallback();
        const countText = this.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
        const count = countText ? Number(countText) : this.querySelector('#cart-drawer-heading-empty') ? 0 : NaN;
        if (!Number.isSafeInteger(count) || count < 0) throw new Error('Unable to confirm the bag count.');
        this.#refreshDrawerState(count);
      } catch (error) {
        console.error(error);
        if (!recoveryAttempted) {
          try {
            await this.#recoverDrawerCart();
            return;
          } catch (recoveryError) {
            console.error(recoveryError);
          }
        }
        this.#showDrawerError(error instanceof Error ? error.message : String(error));
      }
      return;
    }
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
